import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, Calendar, ChevronRight, Globe, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ClipboardEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DomainUnreachableNotice } from './DomainUnreachableNotice';
import { KnownBadUrlNotice } from './KnownBadUrlNotice';
import { OnboardingHero } from './OnboardingHero';
import { PlaceholderDomainNotice } from './PlaceholderDomainNotice';
import { QuickchatDomainNotice } from './QuickchatDomainNotice';
import { isPlaceholderDomain, isQuickchatDomain } from './quickchatDomainCheck';
import { descriptionLength, extractUrlHost } from './useAutomaticOnboarding';
import { OnboardingBrand } from './useGettingStartedOnboarding';
import { UrlInput } from '@/Domain/components/KnowledgeBase/UploadUrlModal/components/UrlInput';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from '@/Domain/components/Shared/Field';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  GettingStartedAutomaticUrlValidatedOutcome,
  PostHogEventEnum,
} from '@/Domain/enum/PostHogEvent.enum';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { checkDomainResolves } from '@/Domain/helper/dnsResolveCheck';
import {
  classifyKnownBadUrl,
  KnownBadUrlMatch,
  parseHost,
  stripHostArtifacts,
} from '@/Domain/helper/scrapeUrlClassifier';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { CAL_LINK, useCalModal } from '@/Domain/hooks/useCalModal.hook';
import { cn } from '@/Domain/utils/cn';

const DESCRIPTION_MIN_LENGTH = 10;
// 2000 is what the sibling URL path already proves safe on the identical LLM
// prompt: onboard_from_url compiles it with page.markdown[:2000] today.
export const DESCRIPTION_MAX_LENGTH = 2000;

// Counter switches to a warning tint here so the ceiling is visible before it bites.
const DESCRIPTION_WARN_LENGTH = Math.round(DESCRIPTION_MAX_LENGTH * 0.9);

// Code-point aware, matching `descriptionLength`, so an emoji is never split in half.
const clampDescription = (value: string): string =>
  Array.from(value).slice(0, DESCRIPTION_MAX_LENGTH).join('');
export const DESCRIPTION_COPY = {
  default: {
    title: 'automatic_flow.description.title',
    label: 'automatic_flow.description.label',
    placeholder: 'automatic_flow.description.placeholder',
    submit: 'automatic_flow.description.submit',
    resultHint: 'automatic_flow.description.result_hint',
    errorPrefix: 'automatic_flow.description.error',
    clamped: 'automatic_flow.description.clamped',
    clampedUndo: 'automatic_flow.description.clamped_undo',
    setupManually: 'automatic_flow.description.setup_manually',
    working: 'automatic_flow.description.working',
    wait: [
      'automatic_flow.description.wait.reading',
      'automatic_flow.description.wait.naming',
      'automatic_flow.description.wait.prompt',
      'automatic_flow.description.wait.guidelines',
      'automatic_flow.description.wait.starters',
      'automatic_flow.description.wait.polishing',
    ],
    generationError: 'automatic_flow.description.generation_error',
    nextStepsTitle: 'automatic_flow.description.next_steps_title',
    nextStepsDescription: 'automatic_flow.description.next_steps_description',
  },
  discord: {
    title: 'automatic_flow.discord.description_title',
    label: 'automatic_flow.discord.description_title',
    placeholder: 'automatic_flow.discord.description_placeholder',
    submit: 'automatic_flow.discord.description_submit',
    resultHint: 'automatic_flow.discord.description_result_hint',
    errorPrefix: 'automatic_flow.discord.description_error',
    clamped: 'automatic_flow.discord.description_clamped',
    clampedUndo: 'automatic_flow.discord.description_clamped_undo',
    setupManually: 'automatic_flow.discord.setup_manually',
    working: 'automatic_flow.discord.description_working',
    wait: [
      'automatic_flow.discord.description_wait.reading',
      'automatic_flow.discord.description_wait.naming',
      'automatic_flow.discord.description_wait.prompt',
      'automatic_flow.discord.description_wait.guidelines',
      'automatic_flow.discord.description_wait.starters',
      'automatic_flow.discord.description_wait.polishing',
    ],
    generationError: 'automatic_flow.discord.description_generation_error',
    nextStepsTitle: 'automatic_flow.discord.description_next_steps_title',
    nextStepsDescription: 'automatic_flow.discord.description_next_steps_description',
  },
} as const;

// Long enough for the pending button to paint and be read, short enough that it
// still feels like the click caused it. The backend run it delays takes ~15-30s.
const SUBMIT_FEEDBACK_HOLD_MS = 550;

// One submit button for both inputs, so the two brands need different ids only
// because their assertions prove different copy sets are in use.
const SUBMIT_TESTID: Record<OnboardingBrand, string> = {
  default: 'gs-automatic-submit',
  discord: 'gs-discord-description-submit',
};

/**
 * What the user has actually typed, which is what the single CTA names. Derived
 * from the drafts rather than from validated values so the label tracks every
 * keystroke instead of waiting for a submit.
 */
type CtaState = 'neither' | 'url' | 'description' | 'both';

const CTA_LABEL_KEY: Record<CtaState, string> = {
  neither: 'automatic_flow.cta.default',
  url: 'automatic_flow.cta.from_website',
  description: 'automatic_flow.cta.from_description',
  // Named rather than falling back to the neutral label: someone who filled both
  // fields should be able to see that both are going to be used, not be left
  // wondering which one the button picked.
  both: 'automatic_flow.cta.from_both',
};

const CTA_ICON: Record<CtaState, LucideIcon> = {
  neither: Sparkles,
  url: Globe,
  description: Sparkles,
  both: Globe,
};

// A scale crossfade reads as "the button re-labelled itself". The y-slide used by
// the progress ticker reads as "a new message arrived", which is wrong here.
const CTA_SWAP_INITIAL = { opacity: 0, scale: 0.95 } as const;
const CTA_SWAP_ANIMATE = { opacity: 1, scale: 1 } as const;
const CTA_SWAP_EXIT = { opacity: 0, scale: 1.05 } as const;
const CTA_SWAP_TRANSITION = { duration: 0.2 } as const;

export type DescriptionValidationOutcome = 'empty' | 'too_short' | 'too_long';

/**
 * Bounds for the description, given whether a website is being submitted with it.
 *
 * The ten-character floor exists because a six-word brief cannot carry a whole
 * Agent on its own. Beside a URL it is not the build: the scraped page is, and the
 * description is supplementary context ("B2B only") that the generator's prompt is
 * explicitly written to treat as optional. Rejecting it there is a dead end for a
 * user who did nothing wrong, so the floor lifts. The ceiling never does.
 */
export function validateDescription(
  value: string,
  { hasUrl }: { hasUrl: boolean },
): DescriptionValidationOutcome | null {
  const trimmed = value.trim();
  if (!trimmed) return hasUrl ? null : 'empty';
  if (descriptionLength(trimmed) > DESCRIPTION_MAX_LENGTH) return 'too_long';
  if (!hasUrl && descriptionLength(trimmed) < DESCRIPTION_MIN_LENGTH) return 'too_short';
  return null;
}

/** Shape check only: empty → whitespace → invalid_format → valid. */
function classifyUrlShape(cleaned: string): GettingStartedAutomaticUrlValidatedOutcome {
  if (!cleaned) return 'empty';
  if (/\s/.test(cleaned)) return 'has_whitespace';
  if (!UploadUrlModalHelper.validateUrl(cleaned)) return 'invalid_format';
  return 'valid';
}

/**
 * Normalize common paste artifacts before validating: trim edge whitespace, plus
 * any whitespace immediately after the auto-prepended `https://`. The latter shows
 * up when a user pastes something like `   quickchat.ai` — `AppHelper.addProtocol`
 * doesn't recognize a whitespace-prefixed string as already-schemed and stamps
 * `https://` on the front. Internal whitespace anywhere else still falls through to
 * the `\s` check in `classifyUrlShape`.
 */
function cleanUrlDraft(draft: string): string {
  const cleaned = stripHostArtifacts(draft.trim().replace(/^(https?:\/\/)\s+/, '$1'));
  // A field the user emptied still round-trips through `AppHelper.addProtocol`, so
  // strip a bare scheme before deciding whether a URL was typed at all.
  return /^https?:\/\/$/.test(cleaned) ? '' : cleaned;
}

/**
 * Secondary CTA rendered under the setup form. Opens the same Cal.com
 * modal that the deleted `OnboardingModeSelection` surfaced — the booking
 * link, theme sync, and PostHog event all stay identical so funnel data
 * comparing the two eras remains meaningful.
 */
export const BookCallOption = ({ disabled = false }: { disabled?: boolean }) => {
  const { t } = useTranslation('getting_started');
  const openCalModal = useCalModal();
  const { capture } = usePostHogAnalytics();
  // The Cal.com modal opens via an async `getCalApi()` round-trip (~100-300ms
  // on a cold load). Without a pending guard the user can fire 3+ clicks
  // before the modal paints — each one fires `getting_started_call_booked`
  // and triggers `cal('modal', ...)`, inflating the funnel metric and
  // queueing duplicate modal-open commands. PostHog production data showed
  // 4 of 27 cohort users emitting 3 call-booked events within 1-2 seconds,
  // confirming the rage-click pattern.
  const isOpeningRef = useRef(false);

  const handleBookCall = async () => {
    if (disabled || isOpeningRef.current) return;
    isOpeningRef.current = true;
    capture(PostHogEventEnum.GettingStartedCallBooked);
    try {
      await openCalModal(CAL_LINK.productDemo, 'getting_started');
    } finally {
      isOpeningRef.current = false;
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={handleBookCall}
      // Not `disabled` for the same reason as the manual link: Button's 50% fade
      // for that state cannot be overridden from here, and only the CTA should
      // visibly react to its own press. `handleBookCall` already returns early.
      aria-disabled={disabled}
      data-testid="gs-automatic-book-call"
      className="w-full"
    >
      <Calendar data-icon="inline-start" />
      {t('automatic_flow.book_call')}
    </Button>
  );
};

/**
 * The entry screen's one form. A user may hand us a website URL, a plain-English
 * description, or both, and there is a single submit button for all three — the
 * two-button version made the next click ambiguous and let someone with a website
 * drift into typing a lazy sentence instead.
 *
 * The Discord brand renders the description half only: it has no website step, so
 * `brand` gates the URL input, the divider and the Book-a-call CTA rather than
 * forking the component.
 */
export const OnboardingSetupForm = ({
  brand,
  onSubmitUrl,
  onSubmitDescription,
  isSubmitting,
  onSwitchToManual,
  urlDraft,
  onUrlDraftChange,
  descriptionDraft,
  onDescriptionDraftChange,
}: {
  brand: OnboardingBrand;
  /** Called with the description too when both fields were filled. */
  onSubmitUrl: (url: string, description?: string) => void;
  onSubmitDescription: (description: string) => void;
  isSubmitting: boolean;
  onSwitchToManual?: () => void;
  /**
   * Drafts are lifted to the page so a Skip → manual → Back round trip keeps what
   * was typed instead of resetting on motion's `key={mode}` remount.
   */
  urlDraft: string;
  onUrlDraftChange: (next: string) => void;
  descriptionDraft: string;
  onDescriptionDraftChange: (next: string) => void;
}) => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  const reduceMotion = useReducedMotion();
  const isDiscord = brand === 'discord';
  const hasUrlField = !isDiscord;
  const copy = DESCRIPTION_COPY[brand];

  const [urlErrorOutcome, setUrlErrorOutcome] =
    useState<GettingStartedAutomaticUrlValidatedOutcome | null>(null);
  const [descriptionError, setDescriptionError] = useState<DescriptionValidationOutcome | null>(
    null,
  );
  // Cleaned URL pending the quickchat-domain notice; null when no warning is showing.
  const [quickchatNoticeUrl, setQuickchatNoticeUrl] = useState<string | null>(null);
  // Placeholder-domain notice (e.g. user submitted `yourcompany.com` verbatim).
  const [placeholderNoticeShown, setPlaceholderNoticeShown] = useState(false);
  // Known-bad URL notice (Discord invites, Telegram channels, social-media profile
  // pages — anything `scrapeUrlClassifier` knows yields empty Firecrawl scrapes).
  const [knownBadUrlMatch, setKnownBadUrlMatch] = useState<KnownBadUrlMatch | null>(null);
  // True while the client-side DoH existence check is in flight.
  const [checkingDomain, setCheckingDomain] = useState(false);
  // Cleaned URL pending the "domain doesn't exist" notice; null when not showing.
  const [unreachableUrl, setUnreachableUrl] = useState<string | null>(null);
  const [holding, setHolding] = useState(false);
  // The full pre-clamp text, kept so the trim stays reversible. Non-null also
  // means the notice is on screen.
  const [clampedFrom, setClampedFrom] = useState<string | null>(null);

  const submittingRef = useRef(false);
  const preflightAttemptRef = useRef(0);
  const holdTimerRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // One busy flag for the whole form. With a single submit there is no sibling
  // choice to cross-lock, so the preflight/pending callbacks the two-form version
  // needed are gone.
  const busy = isSubmitting || checkingDomain || holding;

  // The primary field takes focus on mount: the URL on the default brand, the
  // description on Discord, which has no URL step.
  useEffect(() => {
    if (hasUrlField) inputRef.current?.focus();
    else textareaRef.current?.focus();
  }, [hasUrlField]);

  useEffect(
    () => () => {
      preflightAttemptRef.current += 1;
      if (holdTimerRef.current !== null) window.clearTimeout(holdTimerRef.current);
    },
    [],
  );

  const handleUrlChange = (next: string) => {
    if (urlErrorOutcome) setUrlErrorOutcome(null);
    onUrlDraftChange(next);
  };

  const handleDescriptionChange = (next: string) => {
    if (descriptionError) setDescriptionError(null);
    // Editing makes the stashed original stale, so the offer goes.
    if (clampedFrom !== null) setClampedFrom(null);
    onDescriptionDraftChange(next);
  };

  const captureClamp = (
    action: 'clamped' | 'undone',
    properties: { resulting_length: number; pasted_length?: number },
  ): void => {
    capture(PostHogEventEnum.GettingStartedDescriptionClamped, {
      brand,
      action,
      limit: DESCRIPTION_MAX_LENGTH,
      ...properties,
    });
    if (!isDiscord) return;
    capture(PostHogEventEnum.GettingStartedDiscordDescriptionClamped, {
      action,
      limit: DESCRIPTION_MAX_LENGTH,
      ...properties,
    });
  };

  /**
   * A brief pasted over the limit is kept, not bounced: the first
   * `DESCRIPTION_MAX_LENGTH` characters stay, we say so, and the original is one
   * click away. Typing past the limit is left alone -- the counter turns and
   * submit still reports it -- and the textarea deliberately has no `maxLength`,
   * because the browser would enforce that by trimming the paste silently, which
   * is the failure this replaces.
   */
  const handleDescriptionPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;
    const textarea = event.currentTarget;
    const start = textarea.selectionStart ?? descriptionDraft.length;
    const end = textarea.selectionEnd ?? descriptionDraft.length;
    const merged = descriptionDraft.slice(0, start) + pasted + descriptionDraft.slice(end);
    if (descriptionLength(merged) <= DESCRIPTION_MAX_LENGTH) return;

    event.preventDefault();
    if (descriptionError) setDescriptionError(null);
    onDescriptionDraftChange(clampDescription(merged));
    setClampedFrom(merged);
    captureClamp('clamped', {
      pasted_length: descriptionLength(pasted),
      resulting_length: descriptionLength(merged),
    });
  };

  const undoDescriptionClamp = () => {
    if (clampedFrom === null) return;
    onDescriptionDraftChange(clampedFrom);
    setClampedFrom(null);
    captureClamp('undone', { resulting_length: descriptionLength(clampedFrom) });
  };

  const captureDescriptionValidation = (
    outcome: 'valid' | DescriptionValidationOutcome,
    length: number,
    hasUrl: boolean,
  ): void => {
    capture(PostHogEventEnum.GettingStartedDescriptionValidated, {
      brand,
      outcome,
      description_length: length,
      has_url: hasUrl,
    });
    if (!isDiscord) return;
    capture(PostHogEventEnum.GettingStartedDiscordDescriptionValidated, {
      outcome,
      description_length: length,
    });
  };

  /**
   * `onSubmit*` flips the flow to `processing` in the same tick it dispatches,
   * which unmounts this form before React can paint the pending button. Hold the
   * dispatch so the press reads as accepted — but only for whatever is left of the
   * window, so a submit that already spent 2s in the DoH lookup does not wait again.
   */
  const dispatchAfterHold = (clickedAt: number, dispatch: () => void): void => {
    setHolding(true);
    const remaining = Math.max(0, SUBMIT_FEEDBACK_HOLD_MS - (Date.now() - clickedAt));
    holdTimerRef.current = window.setTimeout(() => {
      holdTimerRef.current = null;
      dispatch();
    }, remaining);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || submittingRef.current) return;

    const cleanedUrl = hasUrlField ? cleanUrlDraft(urlDraft) : '';
    const hasUrl = cleanedUrl !== '';
    const trimmedDescription = descriptionDraft.trim();
    const hasDescription = trimmedDescription !== '';
    const clickedAt = Date.now();

    // --- Phase A: synchronous, both fields, no network -----------------------
    // Running the description bounds before the DoH lookup keeps a 50-2500ms round
    // trip from being spent on a submit that was never going to be dispatched.
    const urlOutcome =
      hasUrlField && (hasUrl || !hasDescription) ? classifyUrlShape(cleanedUrl) : null;
    if (urlOutcome !== null) {
      // Fires on every submit including rejections, so the funnel can split
      // "submitted garbage" from "submitted a real-looking URL the backend later
      // DNS-failed". `url_host` is undefined on rejection branches.
      capture(PostHogEventEnum.GettingStartedAutomaticUrlValidated, {
        outcome: urlOutcome,
        url_host: extractUrlHost(cleanedUrl),
        has_description: hasDescription,
      });
    }
    // Validated when something was typed, and also when the description is the only
    // field there is: on Discord an empty submit has to say so, whereas on the
    // default brand the combined URL error already covers "nothing typed at all".
    const shouldValidateDescription = hasDescription || !hasUrlField;
    const descriptionOutcome = shouldValidateDescription
      ? validateDescription(trimmedDescription, { hasUrl })
      : null;
    if (shouldValidateDescription) {
      captureDescriptionValidation(
        descriptionOutcome ?? 'valid',
        descriptionLength(trimmedDescription),
        hasUrl,
      );
    }

    setUrlErrorOutcome(urlOutcome === 'valid' ? null : urlOutcome);
    setDescriptionError(descriptionOutcome);
    // A typed but malformed URL blocks even when the description is valid. Falling
    // through would build from the description and then report a website source
    // that was never read, hiding the user's typo instead of naming it.
    if ((urlOutcome !== null && urlOutcome !== 'valid') || descriptionOutcome !== null) return;

    // Reflect the cleanup back into the draft so a post-error retry doesn't show
    // the original whitespace-laden value in the field.
    if (hasUrl && cleanedUrl !== urlDraft) onUrlDraftChange(cleanedUrl);

    // --- Phase B: synchronous notices, URL only ------------------------------
    if (hasUrl) {
      if (isQuickchatDomain(cleanedUrl)) {
        capture(PostHogEventEnum.GettingStartedQuickchatDomainWarningShown, {
          url_host: parseHost(cleanedUrl),
        });
        setQuickchatNoticeUrl(cleanedUrl);
        return;
      }
      if (isPlaceholderDomain(cleanedUrl)) {
        capture(PostHogEventEnum.GettingStartedPlaceholderDomainWarningShown, {
          url_host: parseHost(cleanedUrl),
        });
        setPlaceholderNoticeShown(true);
        return;
      }
      const knownBad = classifyKnownBadUrl(cleanedUrl);
      if (knownBad) {
        capture(PostHogEventEnum.GettingStartedKnownBadUrlWarningShown, {
          category: knownBad.category,
          url_host: knownBad.urlHost,
        });
        setKnownBadUrlMatch(knownBad);
        return;
      }
    }

    // --- Phase C: async preflight, URL only ----------------------------------
    if (!hasUrl) {
      submittingRef.current = true;
      dispatchAfterHold(clickedAt, () => onSubmitDescription(trimmedDescription));
      return;
    }

    // Conservative client-side DNS check (see dnsResolveCheck): only a definitive
    // NXDOMAIN stops the user here, turning the typo'd-domain failure — which
    // otherwise surfaces 60-150s later when the background scrape fails — into
    // instant inline feedback. A resolver outage returns 'unknown' and falls
    // straight through to submit.
    const preflightAttempt = ++preflightAttemptRef.current;
    setCheckingDomain(true);
    const resolution = await checkDomainResolves(cleanedUrl).catch(() => 'unknown' as const);
    if (preflightAttempt !== preflightAttemptRef.current) return;
    setCheckingDomain(false);
    if (resolution === 'nxdomain') {
      capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningShown, {
        url_host: parseHost(cleanedUrl),
      });
      setUnreachableUrl(cleanedUrl);
      return;
    }
    submittingRef.current = true;
    dispatchAfterHold(clickedAt, () => onSubmitUrl(cleanedUrl, trimmedDescription || undefined));
  };

  const dismissPlaceholderNotice = () => {
    setPlaceholderNoticeShown(false);
    // Clear the URL only. The description is a separate answer to a separate
    // question and survives every notice round trip.
    onUrlDraftChange('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // quickchat.ai is a hard block: the backend own-domain gate rejects it, so we
  // only offer "correct the URL" — no proceed path that would 400.
  const dismissQuickchatNotice = () => {
    capture(PostHogEventEnum.GettingStartedQuickchatDomainWarningResolved, {
      outcome: 'corrected',
      url_host: quickchatNoticeUrl ? parseHost(quickchatNoticeUrl) : undefined,
    });
    setQuickchatNoticeUrl(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const dismissKnownBadUrlNotice = (outcome: 'corrected' | 'switched_to_manual') => {
    const matched = knownBadUrlMatch;
    capture(PostHogEventEnum.GettingStartedKnownBadUrlWarningResolved, {
      outcome,
      category: matched?.category,
      url_host: matched?.urlHost,
    });
    setKnownBadUrlMatch(null);
    if (outcome === 'switched_to_manual' && onSwitchToManual) {
      onSwitchToManual();
      return;
    }
    onUrlDraftChange('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const dismissUnreachableNotice = (outcome: 'corrected' | 'proceeded' | 'switched_to_manual') => {
    const noticeUrl = unreachableUrl;
    capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningResolved, {
      outcome,
      url_host: noticeUrl ? parseHost(noticeUrl) : undefined,
    });
    setUnreachableUrl(null);
    if (outcome === 'proceeded' && noticeUrl) {
      // Carries the description through: the user is overriding the DNS verdict on
      // the URL, not withdrawing the rest of what they typed.
      submittingRef.current = true;
      const trimmedDescription = descriptionDraft.trim();
      onSubmitUrl(noticeUrl, trimmedDescription || undefined);
      return;
    }
    if (outcome === 'switched_to_manual' && onSwitchToManual) {
      onSwitchToManual();
      return;
    }
    onUrlDraftChange('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  if (quickchatNoticeUrl) {
    return (
      <QuickchatDomainNotice
        submittedUrl={quickchatNoticeUrl}
        onCorrect={() => dismissQuickchatNotice()}
      />
    );
  }

  if (placeholderNoticeShown) {
    return <PlaceholderDomainNotice onBack={dismissPlaceholderNotice} />;
  }

  if (knownBadUrlMatch) {
    return (
      <KnownBadUrlNotice
        category={knownBadUrlMatch.category}
        urlHost={knownBadUrlMatch.urlHost}
        onCorrect={() => dismissKnownBadUrlNotice('corrected')}
        onSwitchToManual={() => dismissKnownBadUrlNotice('switched_to_manual')}
      />
    );
  }

  if (unreachableUrl) {
    return (
      <DomainUnreachableNotice
        urlHost={parseHost(unreachableUrl) ?? unreachableUrl}
        onCorrect={() => dismissUnreachableNotice('corrected')}
        onProceed={() => dismissUnreachableNotice('proceeded')}
        onSwitchToManual={() => dismissUnreachableNotice('switched_to_manual')}
      />
    );
  }

  const descriptionCount = descriptionLength(descriptionDraft);
  const fieldId = isDiscord ? 'gs-discord-description' : 'gs-description';
  const errorId = `${fieldId}-error`;

  // Discord has one input and one meaning, so its CTA stays static; only the
  // default brand has something for the label to follow.
  const typedUrl = hasUrlField && cleanUrlDraft(urlDraft) !== '';
  const typedDescription = descriptionDraft.trim() !== '';
  const ctaState: CtaState =
    typedUrl && typedDescription
      ? 'both'
      : typedUrl
        ? 'url'
        : typedDescription
          ? 'description'
          : 'neither';
  const CtaIcon = isDiscord ? Sparkles : CTA_ICON[ctaState];
  const ctaLabel = busy ? t(copy.working) : isDiscord ? t(copy.submit) : t(CTA_LABEL_KEY[ctaState]);
  // Keyed on the state rather than the label: 'neither' and 'both' share a label
  // but differ by icon, and every single-keystroke transition changes one of them.
  const ctaKey = busy ? 'working' : isDiscord ? 'discord' : ctaState;

  return (
    <form onSubmit={handleSubmit} className={cn('flex flex-col', isDiscord ? 'gap-6' : 'gap-4')}>
      {/* Discord has no website step, so its hero keeps naming the description as
          the task; the default brand's names both inputs. */}
      <OnboardingHero
        brand={brand}
        subtitle={isDiscord ? t(copy.title) : t('automatic_flow.input_title')}
      />

      {/* Interactive controls are bounded to `max-w-md` so the column stays at a
          comfortable typing width even though the outer container went wide to
          give the hero copy room to breathe. */}
      <FieldGroup className="mx-auto w-full max-w-md gap-4">
        {hasUrlField && (
          <>
            {/* Shared `UrlInput` — same component used by the KB tab's
                `UploadUrlModal` and by the manual-onboarding `KnowledgeBaseStep`,
                so the `https://` adornment, focus/error styling and validation
                strings stay in lockstep across all three entry points. */}
            <div className="flex flex-col gap-1">
              <div data-testid="gs-automatic-url-input">
                <UrlInput
                  ref={inputRef}
                  type={WebsiteModalTypeEnum.SINGLE}
                  value={urlDraft}
                  onChange={handleUrlChange}
                  error={urlErrorOutcome !== null}
                  disabled={busy}
                  className="[&>div:first-child]:!px-3"
                />
              </div>
              {/* Fixed-height slot reserved for the validation message: always
                  present (sized for two lines — the longest translated string) so
                  the message fades in without growing the column. Without it the
                  `my-auto`-centered layout re-centers on every error, jumping the
                  hero up and the actions below down. */}
              <div className="min-h-[2.5rem] px-1" aria-live="polite">
                {urlErrorOutcome && (
                  <motion.div
                    role="alert"
                    initial={reduceMotion ? undefined : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                    className="flex flex-col gap-0.5 text-destructive text-sm"
                    data-testid="gs-automatic-url-error"
                  >
                    <span className="flex items-start gap-1.5">
                      <AlertCircle className="size-4 shrink-0 mt-0.5" />
                      <span>{t(`automatic_flow.url_error.${urlErrorOutcome}`)}</span>
                    </span>
                    {/* Only offered when there is actually a description to fall
                        back on, so it never advertises a path the user has not
                        already half-taken. */}
                    {urlErrorOutcome !== 'empty' && descriptionDraft.trim() !== '' && (
                      <span className="pl-[1.375rem]">
                        {t('automatic_flow.url_error.clear_to_use_description')}
                      </span>
                    )}
                  </motion.div>
                )}
              </div>
            </div>

            <FieldSeparator className="tracking-wider" data-testid="gs-automatic-or-divider">
              {t('automatic_flow.divider_describe')}
            </FieldSeparator>
          </>
        )}

        <Field data-invalid={Boolean(descriptionError)} className="gap-2">
          <FieldLabel htmlFor={fieldId} className="sr-only">
            {t(copy.label)}
          </FieldLabel>
          <AutosizeTextarea
            ref={textareaRef}
            id={fieldId}
            value={descriptionDraft}
            onChange={(event) => handleDescriptionChange(event.target.value)}
            onPaste={handleDescriptionPaste}
            aria-invalid={Boolean(descriptionError)}
            aria-describedby={descriptionError ? errorId : undefined}
            placeholder={t(copy.placeholder)}
            minHeight={112}
            maxHeight={220}
            disabled={busy}
            error={Boolean(descriptionError)}
            className={cn(
              // The shared textarea defaults to px-6; the URL input above sits at
              // px-4, so inherit nothing and match it. Discord differs in colour only.
              'px-4',
              isDiscord &&
                'border-[var(--qc-discord-border)] bg-[var(--qc-discord-surface)] text-[var(--qc-discord-on-surface)] placeholder:text-[var(--qc-discord-muted)]',
            )}
            data-testid={`${fieldId}-input`}
          />
          {clampedFrom !== null && (
            <div
              role="status"
              className={cn(
                'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs',
                isDiscord
                  ? 'border-[var(--qc-discord-border)] bg-[var(--qc-discord-surface)] text-[var(--qc-discord-on-surface)]'
                  : 'border-border bg-muted text-foreground',
              )}
              data-testid={`${fieldId}-clamp-notice`}
            >
              <span>{t(copy.clamped, { limit: DESCRIPTION_MAX_LENGTH })}</span>
              <button
                type="button"
                onClick={undoDescriptionClamp}
                // `handleSubmit` snapshots the draft before the preflight and the
                // hold, so an Undo landing in that window repaints the full
                // over-limit text over a submit that already took the prefix.
                disabled={busy}
                className={cn(
                  'shrink-0 cursor-pointer rounded-sm font-medium underline underline-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  'focus-visible:outline-2 focus-visible:outline-offset-2',
                  isDiscord
                    ? 'focus-visible:outline-[var(--qc-discord-blurple)]'
                    : 'focus-visible:outline-ring',
                )}
                data-testid={`${fieldId}-clamp-undo`}
              >
                {t(copy.clampedUndo)}
              </button>
            </div>
          )}
          <div className="flex min-h-10 items-start justify-between gap-3 px-1">
            <div aria-live="polite">
              {descriptionError && (
                <FieldError id={errorId} className="text-destructive" data-testid={errorId}>
                  {t(`${copy.errorPrefix}.${descriptionError}`, {
                    limit: DESCRIPTION_MAX_LENGTH,
                  })}
                </FieldError>
              )}
            </div>
            <span
              className={cn(
                'shrink-0 text-xs',
                descriptionCount > DESCRIPTION_MAX_LENGTH
                  ? 'font-medium text-destructive'
                  : descriptionCount >= DESCRIPTION_WARN_LENGTH
                    ? 'text-warning'
                    : isDiscord
                      ? 'text-[var(--qc-discord-muted)]'
                      : 'text-muted-foreground',
              )}
              data-testid={`${fieldId}-counter`}
            >
              {descriptionCount}/{DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
        </Field>

        {/* One button for every combination of inputs. Never disabled by validity:
            a disabled button cannot explain what is missing, so this one stays
            live and answers on click. */}
        <Button
          size="md"
          type="submit"
          loading={busy}
          variant={isDiscord ? 'custom' : undefined}
          className={cn(
            'w-full transition-transform duration-100 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100',
            isDiscord &&
              'border-0 bg-[var(--qc-discord-blurple)] text-white hover:bg-[var(--qc-discord-blurple-hover)]',
          )}
          data-testid={SUBMIT_TESTID[brand]}
        >
          {/* The button is `w-full` inside a `max-w-md` column, so its box is fixed
              by the layout and the label crossfades inside it without reflow. The
              wrapper carries the gap the button would otherwise provide. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={ctaKey}
              className="inline-flex items-center gap-1.5"
              initial={reduceMotion ? undefined : CTA_SWAP_INITIAL}
              animate={CTA_SWAP_ANIMATE}
              exit={reduceMotion ? undefined : CTA_SWAP_EXIT}
              transition={reduceMotion ? { duration: 0 } : CTA_SWAP_TRANSITION}
            >
              <CtaIcon />
              {ctaLabel}
            </motion.span>
          </AnimatePresence>
        </Button>
        <p
          className={cn(
            'text-center text-xs text-muted-foreground',
            isDiscord && 'text-[var(--qc-discord-muted)]',
          )}
        >
          {/* `loading` keeps the button's label in the DOM but hides it, so the
              working copy is repeated here to stay visible during the run. */}
          {busy ? t(copy.working) : t(copy.resultHint)}
        </p>
        {onSwitchToManual && (
          <Button
            size="sm"
            variant="link"
            type="button"
            // Inert while a submit is in flight, but deliberately not `disabled`:
            // the link variant lightens its text and Button forces a 50% fade for
            // that state, so the link visibly changed the instant the CTA was
            // pressed and read as though the click had landed on it. Button
            // appends its own `disabled:opacity-50` after the caller's classes,
            // so tailwind-merge drops any override — the attribute has to go
            // instead. `aria-disabled` keeps the truth for assistive tech.
            onClick={() => {
              if (busy) return;
              onSwitchToManual();
            }}
            aria-disabled={busy}
            className={cn(
              isDiscord &&
                '!text-[var(--qc-discord-muted)] hover:!text-[var(--qc-discord-on-surface)] hover:!opacity-100',
            )}
            data-testid="gs-automatic-skip"
          >
            {t(copy.setupManually)}
            <ChevronRight data-icon="inline-end" />
          </Button>
        )}
        {hasUrlField && <BookCallOption disabled={busy} />}
      </FieldGroup>
    </form>
  );
};
